import { DataTable, type DataTableSort } from '@bcecloud/portal-ui';
import { DeltaPct, pctChange } from '@/components/traffic-sources';
import type { PageQueryRow } from '@/lib/pages';
import { PositionChange, positionDelta } from '@/components/page-trend';

/**
 * The search-query table both the page detail and the Keywords screen
 * show: clicks, impressions, CTR and Google position with previous-window
 * deltas. A query is a button that opens its trend.
 */

/** Sort accessors for the query rows, for useLocalSort. */
export const QUERY_ACCESSORS: Readonly<Record<string, (row: PageQueryRow) => number | string | null>> = {
  query: (row) => row.query,
  clicks: (row) => row.clicks,
  clicksChange: (row) => pctChange(row.clicks, row.previous?.clicks ?? null),
  impressions: (row) => row.impressions,
  ctr: (row) => row.ctr,
  position: (row) => row.position,
  // current − previous, like every position change: negative is an improvement.
  positionChange: (row) => (row.previous === null ? null : row.position - row.previous.position),
};

export function ctrLabel(ctr: number | null): string {
  return ctr === null ? '—' : `${(ctr * 100).toFixed(1)}%`;
}

/** The shared position change; "new" without a previous window, a dot when flat. */
function PositionDelta({ row }: { readonly row: PageQueryRow }): React.ReactElement {
  if (row.previous === null) return <span className="text-portal-muted">new</span>;
  const moved = positionDelta(row.position, row.previous.position);
  if (moved === null || moved === 0) return <span className="text-portal-muted">•</span>;
  return <PositionChange value={moved} previous={row.previous.position} className="text-portal-micro" />;
}

export function QueryTable({
  rows,
  sort,
  onSort,
  onSelect,
  minWidth = 760,
}: {
  readonly rows: readonly PageQueryRow[];
  readonly sort: DataTableSort;
  readonly onSort: (key: string) => void;
  readonly onSelect: (query: string) => void;
  readonly minWidth?: number;
}): React.ReactElement {
  return (
    <DataTable
      headers={[
        { label: 'Query', sortKey: 'query' },
        { label: 'Clicks', sortKey: 'clicks' },
        { label: 'Δ clicks', sortKey: 'clicksChange' },
        { label: 'Impressions', sortKey: 'impressions' },
        { label: 'CTR', sortKey: 'ctr' },
        { label: 'Position', sortKey: 'position' },
        { label: 'Δ position', sortKey: 'positionChange' },
      ]}
      rightAlignedHeaders={[1, 2, 3, 4, 5, 6]}
      minWidth={minWidth}
      flush
      sort={sort}
      onSort={onSort}
      cards={rows.map((row) => ({
        key: row.query,
        title: row.query,
        fields: [
          { label: 'Clicks', value: <>{row.clicks.toLocaleString()} <DeltaPct current={row.clicks} previous={row.previous?.clicks ?? null} /></> },
          { label: 'Impressions', value: row.impressions.toLocaleString() },
          { label: 'Position', value: <>{row.position.toFixed(1)} <PositionDelta row={row} /></> },
          { label: 'CTR', value: ctrLabel(row.ctr) },
        ],
        onOpen: () => onSelect(row.query),
      }))}
    >
      {rows.map((row) => (
        <tr key={row.query} className="border-t border-portal-line align-middle">
          <td className="p-3 text-portal-small font-bold text-portal-ink">
            <button type="button" onClick={() => onSelect(row.query)} className="cursor-pointer text-left text-brand hover:underline">
              {row.query}
            </button>
          </td>
          <td className="p-3 text-right text-portal-small tabular-nums">{row.clicks.toLocaleString()}</td>
          <td className="p-3 text-right text-portal-small whitespace-nowrap">
            <DeltaPct current={row.clicks} previous={row.previous?.clicks ?? null} />
          </td>
          <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{row.impressions.toLocaleString()}</td>
          <td className="p-3 text-right text-portal-small tabular-nums text-portal-muted">{ctrLabel(row.ctr)}</td>
          <td className="p-3 text-right text-portal-small tabular-nums">{row.position.toFixed(1)}</td>
          <td className="p-3 text-right text-portal-small whitespace-nowrap tabular-nums">
            <PositionDelta row={row} />
          </td>
        </tr>
      ))}
    </DataTable>
  );
}
